import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Box,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Tooltip,
  Checkbox,
  Snackbar,
  IconButton,
  Pagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Radio,
  RadioGroup,
  FormControlLabel,
  Switch,
  CircularProgress,
  ToggleButton,
  ToggleButtonGroup,
  TextField,
  Alert as MuiAlert,
} from '@mui/material';
import Card from './Card';
import Table from './Table';
import Button from './Button';
import Loading from './Loading';
import EmptyState from './EmptyState';
import Alert from './Alert';
import EvidenceReviewModal from './EvidenceReviewModal';
import HealingIcon from '@mui/icons-material/Healing';
import VisibilityIcon from '@mui/icons-material/Visibility';
import FilterListIcon from '@mui/icons-material/FilterList';
import ClearIcon from '@mui/icons-material/Clear';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import NewReleasesIcon from '@mui/icons-material/NewReleases';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import DownloadIcon from '@mui/icons-material/Download';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import MarkFixedModal from './MarkFixedModal';
import { getIssueTypeRenderer, issueTypeLabel } from './deterministic/issueTypeRenderers';
import { hasActiveFilters as anyFilterActive } from '../utils/jobFilters';
import MultiSelectFilter from './MultiSelectFilter';
import {
  filtersOnArchivedToggle,
  reviewStatusOptions,
  ANOMALY_SORT_OPTIONS,
  DEFAULT_ANOMALY_SORT,
  rowAfterClassificationSave,
} from '../utils/anomalyFilters';
import { anomalyHealChip, canTriggerHeal, FALSE_POSITIVE, OUT_OF_SCOPE } from '../utils/healOutcome';

// Verified By after a save, for a modal whose anomaly may not be on this page.
const verifiedByAfterSave = (prev, res) => rowAfterClassificationSave({ verified_by: prev }, '', res).verified_by;

const EMPTY_FILTERS = { retailer: [], retailer_priority: '', attribute: [], page_type: '', issue_type: '', grouping_key: '', assignee: '', review_status: [], verdict: '', first_seen_from: '', first_seen_to: '', verified_from: '', verified_to: '' };

const PAGE_SIZE = 50;

function stripEvidenceQuery(params) {
  ['anomaly', 'occurrence', 'anomaly_id', 'occurrence_id', 'evidence_index'].forEach((key) => params.delete(key));
  return params;
}

function AnomaliesTab() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  // Rule Synthesis links use `anomaly_id`/`occurrence_id`/`evidence_index`;
  // in-app navigation uses `anomaly`/`occurrence`. Accept both so either
  // deep-link shape opens the evidence modal on the right sample.
  const urlAnomalyId = searchParams.get('anomaly') || searchParams.get('anomaly_id') || null;
  const urlOccurrenceParam = searchParams.get('occurrence') || searchParams.get('occurrence_id') || null;
  const rawEvidenceIndex = searchParams.get('evidence_index');
  const urlEvidenceIndex = rawEvidenceIndex !== null && rawEvidenceIndex !== '' ? parseInt(rawEvidenceIndex, 10) : null;
  const deepLinkTarget = useMemo(() => {
    const hasIndex = Number.isInteger(urlEvidenceIndex);
    if (!urlOccurrenceParam && !hasIndex) return null;
    return { occurrenceId: urlOccurrenceParam, evidenceIndex: hasIndex ? urlEvidenceIndex : null };
  }, [urlOccurrenceParam, urlEvidenceIndex]);
  const [anomalies, setAnomalies] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Filters
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [filterOptions, setFilterOptions] = useState({ retailers: [], attributes: [], page_types: [], issue_types: [], verdicts: [], grouping_keys: [], priority_tiers: [] });
  const [users, setUsers] = useState([]);
  const [sourceMode, setSourceMode] = useState('llm_validated');
  // Server-side list order (DPS-4718); not a filter, so Clear leaves it alone.
  const [sortOrder, setSortOrder] = useState(DEFAULT_ANOMALY_SORT);

  // Modal state
  const [imagePreviewOpen, setImagePreviewOpen] = useState(false);
  const [selectedEvidence, setSelectedEvidence] = useState([]);
  const [evidenceAnomalyId, setEvidenceAnomalyId] = useState(null);
  const [selectedEvidenceMeta, setSelectedEvidenceMeta] = useState({ retailer: '', attribute: '', pageType: '' });
  const [nonNullEvidenceOpen, setNonNullEvidenceOpen] = useState(false);
  const [selectedNonNullEvidence, setSelectedNonNullEvidence] = useState([]);
  const [selectedNonNullMeta, setSelectedNonNullMeta] = useState({ retailer: '', attribute: '', pageType: '' });
  const [nonNullAnomalyId, setNonNullAnomalyId] = useState(null);

  // Tracks which anomaly the URL-read effect last applied so a later
  // `anomalies` list refresh doesn't re-open / refetch the same modal.
  const appliedSelectionRef = useRef({ anomalyId: null });

  // Bulk selection
  const [selectedAnomalies, setSelectedAnomalies] = useState(new Set());
  const [bulkAssigning, setBulkAssigning] = useState(false);
  const [autoDistributeOpen, setAutoDistributeOpen] = useState(false);
  const [selectedDistributeUsers, setSelectedDistributeUsers] = useState([]);

  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [healingInProgress, setHealingInProgress] = useState(null);

  // Conflict resolution dialog state
  const [conflictDialog, setConflictDialog] = useState({ open: false, anomaly: null, chosen: '' });
  const [showConflictsOnly, setShowConflictsOnly] = useState(false);
  const [showNeedsRevalidationOnly, setShowNeedsRevalidationOnly] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [showClosed, setShowClosed] = useState(false);

  // Mark Fixed modal state
  const [markFixedModal, setMarkFixedModal] = useState({ open: false, anomaly: null });
  const [detailDialog, setDetailDialog] = useState({ open: false, anomaly: null });
  const [classifyDialog, setClassifyDialog] = useState({ open: false, anomaly: null, verdict: 'true_anomaly', reason: '' });
  const [classifying, setClassifying] = useState(false);

  const isDeterministic = sourceMode === 'deterministic';

  // Sample classifications parsed
  const sampleClassifications = useMemo(() => {
    const map = {};
    anomalies.forEach((a) => {
      if (a.sample_classifications) {
        try { map[a.id] = JSON.parse(a.sample_classifications); } catch { /* ignore */ }
      }
    });
    return map;
  }, [anomalies]);

  // What the list shows, minus paging; the CSV export sends the same (DPS-4722).
  const listQuery = useMemo(() => ({
    ...filters,
    sort: sortOrder === DEFAULT_ANOMALY_SORT ? undefined : sortOrder,
    verdict_status: showConflictsOnly ? 'conflict' : undefined,
    archived_only: showArchived,
    closed_only: showClosed,
    needs_revalidation: showNeedsRevalidationOnly,
    source: sourceMode,
    grouping_key: filters.grouping_key || undefined,
  }), [filters, showArchived, showClosed, showNeedsRevalidationOnly, showConflictsOnly, sourceMode, sortOrder]);

  const [exporting, setExporting] = useState(false);
  const exportCsv = async () => {
    setExporting(true);
    try {
      const { truncated, total: exportTotal, cap } = await api.downloadAnomaliesCsv(listQuery);
      if (truncated) {
        // Over the row cap, or the Healed filter only checked its first candidates.
        const message = exportTotal > cap
          ? `Exported the first ${cap} of ${exportTotal} anomalies (export limit).`
          : 'Export may be incomplete: the Healed filter checks a limited number of anomalies.';
        setSnackbar({ open: true, message, severity: 'warning' });
      }
    } catch (err) {
      setSnackbar({ open: true, message: err.message || 'Failed to export anomalies', severity: 'error' });
    } finally {
      setExporting(false);
    }
  };

  const fetchAnomalies = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await api.getAnomalies({
        ...listQuery,
        limit: PAGE_SIZE,
        offset: (page - 1) * PAGE_SIZE,
      });
      setAnomalies(data.items || []);
      setTotal(data.total || 0);
      if (showConflictsOnly && (data.total || 0) === 0) {
        setShowConflictsOnly(false);
      }
    } catch (err) {
      setError(err.message || 'Failed to load anomalies');
    } finally {
      setLoading(false);
    }
  }, [listQuery, page, showConflictsOnly]);

  useEffect(() => { fetchAnomalies(); }, [fetchAnomalies]);

  useEffect(() => {
    const pages = Math.max(1, Math.ceil(total / PAGE_SIZE) || 1);
    if (page > pages) setPage(pages);
  }, [total, page]);

  useEffect(() => {
    setSelectedAnomalies(new Set());
  }, [page, filters, showArchived, showClosed, showNeedsRevalidationOnly, showConflictsOnly, sourceMode, sortOrder]);

  useEffect(() => {
    api.getAnomalyFilters({ source: sourceMode }).then((opts) => {
      setFilterOptions({ grouping_keys: [], ...opts });
      // Skip the default "True Anomaly" verdict when arriving via a deep link —
      // the target anomaly may not be a true anomaly, and the default would
      // hide it from the current page (cold-fetch still opens the modal, but
      // the row would be missing from the table).
      const hasDeepLink = !!(searchParams.get('anomaly') || searchParams.get('anomaly_id'));
      if (!hasDeepLink && sourceMode === 'llm_validated' && opts.verdicts && opts.verdicts.length > 0) {
        const trueAnomaly = opts.verdicts.find((v) => v.toLowerCase().includes('true'));
        if (trueAnomaly) {
          setFilters((prev) => (prev.verdict ? prev : { ...prev, verdict: trueAnomaly }));
        }
      }
    }).catch(() => {});
    api.getAssignableUsers().then((u) => setUsers(u || [])).catch(() => {});
  }, [sourceMode]);

  const handleFilterChange = (field) => (e) => {
    const val = e.target.value;
    setFilters((prev) => ({ ...prev, [field]: val }));
    setPage(1);
  };

  const clearFilters = () => {
    setFilters(EMPTY_FILTERS);
    setPage(1);
  };

  const hasActiveFilters = anyFilterActive(filters);

  const handleSourceModeChange = (_event, next) => {
    if (!next) return;
    setSourceMode(next);
    setFilters(EMPTY_FILTERS);
    setPage(1);
    setShowConflictsOnly(false);
  };

  const submitClassify = async () => {
    const { anomaly, verdict, reason } = classifyDialog;
    if (!anomaly) return;
    if (verdict === 'false_positive' && !String(reason || '').trim()) return;
    setClassifying(true);
    try {
      await api.classifyAnomaly(anomaly.id, { verdict, reason });
      setClassifyDialog({ open: false, anomaly: null, verdict: 'true_anomaly', reason: '' });
      setSnackbar({ open: true, message: 'Classification saved', severity: 'success' });
      fetchAnomalies();
    } catch (err) {
      setSnackbar({ open: true, message: err.message || 'Failed to classify', severity: 'error' });
    } finally {
      setClassifying(false);
    }
  };

  // Evidence modal helpers
  //
  // `applyEvidenceSelection` does the actual state-setting to open a modal on
  // a given anomaly row (plus an optional occurrence to restore). It is the
  // shared target of both the click-to-open path (openEvidenceModal, which
  // just writes the URL) and the URL-read effect below (which is the one
  // place that actually calls this).
  const applyEvidenceSelection = useCallback((anomaly) => {
    const issueType = anomaly.issue_type || '';
    if (issueType === 'non-null-issues') {
      try {
        const evidence = JSON.parse(anomaly.evidence_with_reasoning || '[]');
        setSelectedNonNullEvidence(evidence);
        setSelectedNonNullMeta({ retailer: anomaly.retailer, attribute: anomaly.attribute, pageType: anomaly.page_type, verifiedBy: anomaly.verified_by || null });
        setNonNullAnomalyId(anomaly.id);
        if (!sampleClassifications[anomaly.id]) {
          const initialized = evidence.map((e) => {
            const isAbsent = (e.presence || '').toLowerCase() === 'absent';
            return isAbsent ? { verdict: 'skipped', fpReason: '' } : { verdict: '', fpReason: '' };
          });
          setAnomalies((prev) => prev.map((a) =>
            a.id === anomaly.id ? { ...a, sample_classifications: JSON.stringify(initialized) } : a
          ));
        }
        setImagePreviewOpen(false); // the URL can flip `anomaly` between the two issue types with no
        setNonNullEvidenceOpen(true); // intervening close — never let both modals be open at once
      } catch { /* ignore */ }
    } else {
      try {
        const evidence = JSON.parse(anomaly.evidence_with_reasoning || '[]');
        setSelectedEvidence(evidence);
        setEvidenceAnomalyId(anomaly.id);
        setSelectedEvidenceMeta({ retailer: anomaly.retailer, attribute: anomaly.attribute, pageType: anomaly.page_type, verifiedBy: anomaly.verified_by || null });
        // Auto-skip absent samples so user only needs to classify "present" ones
        if (!sampleClassifications[anomaly.id]) {
          const initialized = evidence.map((e) => {
            const isAbsent = (e.presence || '').toLowerCase() === 'absent';
            return isAbsent ? { verdict: 'skipped', fpReason: '' } : { verdict: '', fpReason: '' };
          });
          // Store locally so modal picks it up
          setAnomalies((prev) => prev.map((a) =>
            a.id === anomaly.id ? { ...a, sample_classifications: JSON.stringify(initialized) } : a
          ));
        }
        setNonNullEvidenceOpen(false); // see the matching comment in the non-null branch above
        setImagePreviewOpen(true);
      } catch { /* ignore */ }
    }
  }, [sampleClassifications]);

  // Public opener: writes the URL. The read effect below is what actually
  // calls applyEvidenceSelection — keeping a single write path in sync with
  // a single read path is what makes browser back/forward work for free.
  const openEvidenceModal = useCallback((anomaly) => {
    setSearchParams((prev) => {
      const next = stripEvidenceQuery(new URLSearchParams(prev));
      next.set('anomaly', anomaly.id);
      return next;
    });
  }, [setSearchParams]);

  const closeEvidenceModal = useCallback(() => {
    setSearchParams((prev) => stripEvidenceQuery(new URLSearchParams(prev)));
  }, [setSearchParams]);

  // URL -> state: the single place that reacts to `?anomaly=&occurrence=`
  // (and the Rule Synthesis aliases `anomaly_id`/`occurrence_id`).
  useEffect(() => {
    const anomalyId = urlAnomalyId;

    if (!anomalyId) {
      appliedSelectionRef.current = { anomalyId: null };
      setImagePreviewOpen(false);
      setNonNullEvidenceOpen(false);
      return;
    }

    if (appliedSelectionRef.current.anomalyId === anomalyId) {
      // Same anomaly already applied — occurrence changes flow through
      // `deepLinkTarget` (derived from the URL) without re-running
      // applyEvidenceSelection or refetching anything.
      return;
    }

    const warmRow = anomalies.find((a) => a.id === anomalyId);
    if (warmRow) {
      appliedSelectionRef.current = { anomalyId };
      applyEvidenceSelection(warmRow);
      return;
    }

    // Cold deep link: the anomaly isn't in the currently fetched/filtered
    // page, so fetch it directly instead of leaving the modal closed.
    let cancelled = false;
    appliedSelectionRef.current = { anomalyId };
    api.getAnomaly(anomalyId).then((row) => {
      if (cancelled) return;
      applyEvidenceSelection(row);
    }).catch(() => {
      if (cancelled) return;
      appliedSelectionRef.current = { anomalyId: null };
      setSnackbar({ open: true, message: 'Anomaly not found', severity: 'error' });
      setSearchParams((prev) => stripEvidenceQuery(new URLSearchParams(prev)), { replace: true });
    });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, anomalies, urlAnomalyId]);

  // Occurrence dropdown inside the open modal writes back to the URL —
  // push, so back steps through occurrence changes.
  const handleEvidenceOccurrenceChange = useCallback((occurrenceId) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (occurrenceId) {
        next.set('occurrence', occurrenceId);
        next.delete('occurrence_id');
      } else {
        next.delete('occurrence');
        next.delete('occurrence_id');
      }
      next.delete('evidence_index');
      return next;
    });
  }, [setSearchParams]);

  // A modal calls this (instead of handleEvidenceOccurrenceChange) when it's
  // silently correcting a bad `occurrence` param it found in the URL, rather
  // than reflecting a user-driven pick — replace, so a cold deep link with a
  // stale occurrence id doesn't push a history entry that back would just
  // land back on (re-triggering the same correction).
  const handleEvidenceOccurrenceInvalid = useCallback(() => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('occurrence');
      next.delete('occurrence_id');
      next.delete('evidence_index');
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  // Verified By for the open evidence modal: the live row (updated on save)
  // wins; a cold deep-linked anomaly not on this page falls back to the
  // value captured when the modal opened.
  const verifiedByFor = (anomalyId, meta) => {
    const row = anomalyId ? anomalies.find((a) => a.id === anomalyId) : null;
    return row ? (row.verified_by || null) : (meta?.verifiedBy || null);
  };

  // Match server filter: persisted anomaly.review_status is source of truth.
  // Do not recompute from sample JSON — that caused FP/reviewed badges on
  // rows that still have null review_status (and thus pass "unreviewed").
  const getReviewVerdict = useCallback((anomalyId) => {
    const anomaly = anomalies.find((a) => a.id === anomalyId);
    const status = anomaly?.review_status;
    if (status === 'true_anomaly' || status === 'false_positive' || status === 'reviewed') {
      return status;
    }
    return null;
  }, [anomalies]);

  // Assign handler
  const handleAssign = async (anomalyId, email) => {
    try {
      await api.assignAnomaly(anomalyId, email || null);
      setAnomalies((prev) => prev.map((a) => a.id === anomalyId ? { ...a, assignee: email || null } : a));
      setSnackbar({ open: true, message: email ? `Assigned to ${email}` : 'Unassigned', severity: 'success' });
    } catch (err) {
      setSnackbar({ open: true, message: err.message || 'Failed to assign', severity: 'error' });
    }
  };

  // Bulk assign — assign all selected to one user (or unassign)
  const handleBulkAssign = async (email) => {
    if (selectedAnomalies.size === 0) return;
    const count = selectedAnomalies.size;
    const ids = [...selectedAnomalies];
    setBulkAssigning(true);
    try {
      await api.bulkAssignAnomalies(ids, email || null);
      setAnomalies((prev) => prev.map((a) =>
        ids.includes(a.id) ? { ...a, assignee: email || null } : a
      ));
      setSelectedAnomalies(new Set());
      setAutoDistributeOpen(false);
      setSelectedDistributeUsers([]);
      setSnackbar({
        open: true,
        message: email
          ? `${count} anomalies assigned to ${email}`
          : `${count} anomalies unassigned`,
        severity: 'success',
      });
    } catch (err) {
      setSnackbar({ open: true, message: err.message || 'Failed to bulk assign', severity: 'error' });
    } finally {
      setBulkAssigning(false);
    }
  };

  const toggleDistributeUser = (email) => {
    setSelectedDistributeUsers((prev) =>
      prev.includes(email) ? prev.filter((e) => e !== email) : [...prev, email]
    );
  };

  // Split selected anomalies evenly across chosen peers
  const handleAutoDistribute = async () => {
    if (selectedAnomalies.size === 0 || selectedDistributeUsers.length === 0) return;
    const count = selectedAnomalies.size;
    const anomalyIds = [...selectedAnomalies];
    const userCount = selectedDistributeUsers.length;
    const chunkSize = Math.ceil(anomalyIds.length / userCount);

    setBulkAssigning(true);
    try {
      const assignments = [];
      for (let i = 0; i < userCount; i++) {
        const chunk = anomalyIds.slice(i * chunkSize, Math.min((i + 1) * chunkSize, anomalyIds.length));
        if (chunk.length > 0) {
          assignments.push({ ids: chunk, email: selectedDistributeUsers[i] });
        }
      }

      for (const assignment of assignments) {
        await api.bulkAssignAnomalies(assignment.ids, assignment.email);
      }

      const emailMap = {};
      assignments.forEach(({ ids, email }) => {
        ids.forEach((id) => { emailMap[id] = email; });
      });

      setAnomalies((prev) => prev.map((a) =>
        emailMap[a.id] ? { ...a, assignee: emailMap[a.id] } : a
      ));
      setSelectedAnomalies(new Set());
      setAutoDistributeOpen(false);
      setSelectedDistributeUsers([]);
      setSnackbar({
        open: true,
        message: `Distributed ${count} anomalies across ${userCount} users`,
        severity: 'success',
      });
    } catch (err) {
      setSnackbar({ open: true, message: err.message || 'Distribution failed', severity: 'error' });
    } finally {
      setBulkAssigning(false);
    }
  };

  const toggleSelectAll = () => {
    if (selectedAnomalies.size === paginatedAnomalies.length) {
      setSelectedAnomalies(new Set());
    } else {
      setSelectedAnomalies(new Set(paginatedAnomalies.map((a) => a.id)));
    }
  };

  const selectUnassignedOnPage = () => {
    setSelectedAnomalies(new Set(paginatedAnomalies.filter((a) => !a.assignee).map((a) => a.id)));
  };

  // Conflict resolution
  const openConflictDialog = (anomaly) => {
    setConflictDialog({ open: true, anomaly, chosen: anomaly.verdict || '' });
  };

  const handleResolveConflict = async () => {
    const { anomaly, chosen } = conflictDialog;
    if (!anomaly || !chosen) return;
    try {
      await api.resolveAnomalyConflict(anomaly.id, chosen);
      setConflictDialog({ open: false, anomaly: null, chosen: '' });
      setSnackbar({ open: true, message: 'Conflict resolved', severity: 'success' });
      await fetchAnomalies();
    } catch (err) {
      setSnackbar({ open: true, message: err.message || 'Failed to resolve conflict', severity: 'error' });
    } finally {
      setConflictDialog({ open: false, anomaly: null, chosen: '' });
    }
  };

  // Dismiss revalidation
  const handleDismissRevalidation = async (anomalyId) => {
    try {
      await api.dismissRevalidation(anomalyId);
      setAnomalies((prev) => prev.map((a) =>
        a.id === anomalyId ? { ...a, needs_revalidation: false } : a
      ));
      setSnackbar({ open: true, message: 'Revalidation dismissed', severity: 'success' });
      if (showNeedsRevalidationOnly) fetchAnomalies();
    } catch (err) {
      setSnackbar({ open: true, message: err.message || 'Failed to dismiss', severity: 'error' });
    }
  };

  // Heal — check for active jobs, then send only true anomaly samples.
  // Sample classifications live on occurrences (and are not on the list payload),
  // so we always load occurrences here — do not gate on in-memory list state.
  const handleHeal = async (anomaly) => {
    setHealingInProgress(anomaly.id);

    // Check for existing active healing job (family-aware via retailer+attribute)
    try {
      const activeCheck = await api.getActiveHealJob(anomaly.id, {
        retailer: anomaly.retailer,
        attribute: anomaly.attribute,
      });
      if (activeCheck.has_active_job) {
        const statusLabel = { pending: 'Pending', running: 'Running', awaiting_review: 'Awaiting Review' };
        const ownerHint = activeCheck.retailer && activeCheck.retailer !== anomaly.retailer
          ? ` on related retailer ${activeCheck.retailer}`
          : '';
        setSnackbar({
          open: true,
          message: `An active healing job already exists for this config family${ownerHint} (Status: ${statusLabel[activeCheck.status] || activeCheck.status}). Please wait for it to complete or reject it first.`,
          severity: 'warning',
        });
        setHealingInProgress(null);
        return;
      }
    } catch {
      // If check fails, proceed anyway
    }

    // Aggregate true anomaly samples from ALL occurrences (any date)
    let trueAnomalySamples = [];
    let falsePositiveSamples = [];
    try {
      const occurrences = await api.getAnomalyOccurrences(anomaly.id);
      for (const occ of occurrences) {
        if (!occ.sample_classifications || !occ.evidence_with_reasoning) continue;
        let occEvidence = [];
        let occClassifications = [];
        try { occEvidence = JSON.parse(occ.evidence_with_reasoning); } catch { continue; }
        try { occClassifications = JSON.parse(occ.sample_classifications); } catch { continue; }

        // If classifications are shorter than evidence, they correspond to present-only items
        let merged;
        if (occClassifications.length < occEvidence.length) {
          const presentIndices = occEvidence
            .map((e, i) => ((e.presence || '').toLowerCase() !== 'absent' ? i : -1))
            .filter((i) => i >= 0);
          merged = occClassifications.map((c, ci) => {
            const evidenceIdx = presentIndices[ci] !== undefined ? presentIndices[ci] : ci;
            return { ...(occEvidence[evidenceIdx] || {}), ...c };
          });
        } else {
          merged = occClassifications.map((c, i) => ({ ...(occEvidence[i] || {}), ...c }));
        }
        trueAnomalySamples.push(...merged.filter((s) => s.verdict === 'true_anomaly'));
        falsePositiveSamples.push(...merged.filter((s) => s.verdict === 'false_positive'));
      }
    } catch {
      // Fallback: list payload / local modal state (sample_classifications is not on AnomalyResponse)
      const classifications = sampleClassifications[anomaly.id] || [];
      let evidence = [];
      try { evidence = JSON.parse(anomaly.evidence_with_reasoning || '[]'); } catch { /* ignore */ }
      const merged = classifications.map((c, i) => ({ ...(evidence[i] || {}), ...c }));
      trueAnomalySamples = merged.filter((s) => s.verdict === 'true_anomaly');
      falsePositiveSamples = merged.filter((s) => s.verdict === 'false_positive');
    }

    // Deduplicate by URL (+ product_id for search tiles that share a page).
    // Search-page records need product_id / product_name / page_value to locate
    // their tile; detail-page records simply carry them as undefined.
    // Older search-null rows stored the find in page_value only; use that as
    // attribute_value when the latter is missing so the agent still has a target.
    const seenKeys = new Set();
    const healSampleKey = (s) => {
      const url = s.url || '';
      const productId = s.product_id != null && String(s.product_id).trim() !== '' ? String(s.product_id) : '';
      return productId ? `${url}::${productId}` : url;
    };
    const healAttributeValue = (s) => {
      const av = s.attribute_value;
      if (av != null && String(av).trim() !== '') return av;
      return s.page_value ?? av;
    };
    trueAnomalySamples = trueAnomalySamples
      .map((s) => ({ url: s.url, attribute_value: healAttributeValue(s), reasoning: s.reasoning, detected_via: s.detected_via, confidence: s.confidence, presence: s.presence, product_id: s.product_id, product_name: s.product_name, page_value: s.page_value }))
      .filter((s) => { const key = healSampleKey(s); if (!s.url || seenKeys.has(key)) return false; seenKeys.add(key); return true; });
    falsePositiveSamples = falsePositiveSamples
      .map((s) => ({ url: s.url, reason: s.fp_reason || s.fpReason || 'Marked as false positive', attribute_value: healAttributeValue(s), reasoning: s.reasoning, detected_via: s.detected_via, confidence: s.confidence, presence: s.presence, product_id: s.product_id, product_name: s.product_name, page_value: s.page_value }))
      .filter((s) => { const key = healSampleKey(s); if (!s.url || seenKeys.has(key)) return false; seenKeys.add(key); return true; });

    if (trueAnomalySamples.length === 0) {
      setSnackbar({ open: true, message: 'No true anomaly samples to heal', severity: 'warning' });
      setHealingInProgress(null);
      return;
    }

    try {
      await api.triggerHealJob({
        anomalyId: anomaly.id,
        reportId: anomaly.report_id || '',
        retailer: anomaly.retailer,
        attribute: anomaly.attribute,
        pageType: anomaly.page_type || 'detail',
        issueType: anomaly.issue_type || 'null-issues',
        records: trueAnomalySamples,
        falsePositives: falsePositiveSamples,
      });
      setSnackbar({ open: true, message: 'Healing job triggered successfully', severity: 'success' });
    } catch (err) {
      setSnackbar({ open: true, message: err.message || 'Failed to trigger healing', severity: 'error' });
    } finally {
      setHealingInProgress(null);
    }
  };

  // Server returns the current page; `total` is the full match count
  const paginatedAnomalies = anomalies;
  const totalFiltered = total;
  const totalPages = Math.max(1, Math.ceil(totalFiltered / PAGE_SIZE) || 1);

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  // Global conflict count — fetched independently of filters so the banner always shows
  const [conflictCount, setConflictCount] = useState(0);
  useEffect(() => {
    api.getAnomalies({ verdict_status: 'conflict', limit: 1, offset: 0 }).then((data) => {
      setConflictCount(data.total || 0);
    }).catch(() => {});
  }, [anomalies]); // re-check when anomalies change (e.g. after resolving a conflict)

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', gap: 2 }}>
      <ToggleButtonGroup
        exclusive
        size="small"
        value={sourceMode}
        onChange={handleSourceModeChange}
        sx={{
          alignSelf: 'flex-start',
          p: 0.5,
          gap: 0.25,
          backgroundColor: 'rgba(30, 41, 59, 0.9)',
          border: '1px solid rgba(148, 163, 184, 0.18)',
          borderRadius: '10px',
          '& .MuiToggleButtonGroup-grouped': {
            m: 0,
            border: 'none',
            borderRadius: '8px !important',
            '&:not(:first-of-type)': {
              ml: 0,
              borderLeft: 'none',
            },
          },
          '& .MuiToggleButton-root': {
            textTransform: 'none',
            fontWeight: 600,
            fontSize: '0.8125rem',
            letterSpacing: 0,
            color: '#94a3b8',
            px: 2,
            py: 0.75,
            lineHeight: 1.25,
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.04)',
            },
            '&.Mui-selected': {
              color: '#fff',
              backgroundColor: 'rgba(59, 130, 246, 0.28)',
              boxShadow: 'inset 0 0 0 1px rgba(96, 165, 250, 0.35)',
              '&:hover': {
                backgroundColor: 'rgba(59, 130, 246, 0.38)',
              },
            },
          },
        }}
      >
        <ToggleButton value="llm_validated">LLM-validated</ToggleButton>
        <ToggleButton value="deterministic">Deterministic</ToggleButton>
      </ToggleButtonGroup>

      {/* Conflict Warning Banner */}
      {conflictCount > 0 && !isDeterministic && (
        <MuiAlert
          severity="warning"
          sx={{
            borderLeft: '4px solid #f59e0b',
            '& .MuiAlert-icon': { color: '#f59e0b' },
          }}
          action={
            <Button
              size="small"
              variant={showConflictsOnly ? 'contained' : 'outlined'}
              color="warning"
              onClick={() => {
                const toggling = !showConflictsOnly;
                setShowConflictsOnly(toggling);
                if (toggling) {
                  setFilters((prev) => ({ ...prev, verdict: '', review_status: [] }));
                }
                setPage(1);
              }}
            >
              {showConflictsOnly ? 'Show All' : 'Show Conflicts'}
            </Button>
          }
        >
          <Typography variant="body2" fontWeight={600}>
            {conflictCount} anomal{conflictCount === 1 ? 'y has' : 'ies have'} verdict conflicts that need resolution.
          </Typography>
          <Typography variant="caption" color="text.secondary">
            These anomalies received different system verdicts across occurrences. Resolve them before healing.
          </Typography>
        </MuiAlert>
      )}

      {/* Filters */}
      <Card>
        <Box sx={{ p: 2, pb: 1 }}>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
            <FilterListIcon color="action" />
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>Retailer</InputLabel>
              <MultiSelectFilter label="Retailer" value={filters.retailer} options={filterOptions.retailers} onChange={handleFilterChange('retailer')} />
            </FormControl>
            {(filterOptions.priority_tiers || []).length > 0 && (
              <FormControl size="small" sx={{ minWidth: 170 }}>
                <InputLabel>Priority retailers</InputLabel>
                <Select value={filters.retailer_priority} label="Priority retailers" onChange={handleFilterChange('retailer_priority')}>
                  <MenuItem value="">All retailers</MenuItem>
                  <MenuItem value="top">{`Priority only (${filterOptions.priority_tiers.join(' + ')})`}</MenuItem>
                  {filterOptions.priority_tiers.map((t) => <MenuItem key={t} value={t}>{`${t} only`}</MenuItem>)}
                </Select>
              </FormControl>
            )}
            {!isDeterministic && (
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>Attribute</InputLabel>
              <MultiSelectFilter label="Attribute" value={filters.attribute} options={filterOptions.attributes} onChange={handleFilterChange('attribute')} />
            </FormControl>
            )}
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Page Type</InputLabel>
              <Select value={filters.page_type} label="Page Type" onChange={handleFilterChange('page_type')}>
                <MenuItem value="">All</MenuItem>
                {filterOptions.page_types.map((p) => <MenuItem key={p} value={p}>{p}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 130 }}>
              <InputLabel>Issue Type</InputLabel>
              <Select value={filters.issue_type} label="Issue Type" onChange={handleFilterChange('issue_type')}>
                <MenuItem value="">All</MenuItem>
                {filterOptions.issue_types.map((t) => (
                  <MenuItem key={t} value={t}>{issueTypeLabel(t)}</MenuItem>
                ))}
              </Select>
            </FormControl>
            {isDeterministic && (
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>Grouping key</InputLabel>
                <Select value={filters.grouping_key} label="Grouping key" onChange={handleFilterChange('grouping_key')}>
                  <MenuItem value="">All</MenuItem>
                  {(filterOptions.grouping_keys || []).map((k) => <MenuItem key={k} value={k}>{k}</MenuItem>)}
                </Select>
              </FormControl>
            )}
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>Assignee</InputLabel>
              <Select value={filters.assignee} label="Assignee" onChange={handleFilterChange('assignee')}>
                <MenuItem value="">All</MenuItem>
                <MenuItem value="__unassigned__">Unassigned</MenuItem>
                {users.map((u) => <MenuItem key={u.email} value={u.email}>{u.name || u.email}</MenuItem>)}
              </Select>
            </FormControl>
            {!isDeterministic && (
            <FormControl size="small" sx={{ minWidth: 130 }}>
              <InputLabel>Verdict</InputLabel>
              <Select value={filters.verdict} label="Verdict" onChange={handleFilterChange('verdict')}>
                <MenuItem value="">All</MenuItem>
                {filterOptions.verdicts.map((v) => <MenuItem key={v} value={v}>{v}</MenuItem>)}
              </Select>
            </FormControl>
            )}
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>Review Status</InputLabel>
              <MultiSelectFilter
                label="Review Status"
                value={filters.review_status}
                options={reviewStatusOptions(showArchived)}
                onChange={handleFilterChange('review_status')}
              />
            </FormControl>
            <TextField
              size="small"
              type="date"
              label="First seen from (UTC)"
              value={filters.first_seen_from}
              onChange={handleFilterChange('first_seen_from')}
              InputLabelProps={{ shrink: true }}
              inputProps={{ max: filters.first_seen_to || undefined, 'aria-label': 'First seen from (UTC)' }}
              sx={{ width: 160 }}
            />
            <TextField
              size="small"
              type="date"
              label="First seen to (UTC)"
              value={filters.first_seen_to}
              onChange={handleFilterChange('first_seen_to')}
              InputLabelProps={{ shrink: true }}
              inputProps={{ min: filters.first_seen_from || undefined, 'aria-label': 'First seen to (UTC)' }}
              sx={{ width: 160 }}
            />
            <TextField
              size="small"
              type="date"
              label="Reviewed from (UTC)"
              value={filters.verified_from}
              onChange={handleFilterChange('verified_from')}
              InputLabelProps={{ shrink: true }}
              inputProps={{ max: filters.verified_to || undefined, 'aria-label': 'Reviewed from (UTC)' }}
              sx={{ width: 160 }}
            />
            <TextField
              size="small"
              type="date"
              label="Reviewed to (UTC)"
              value={filters.verified_to}
              onChange={handleFilterChange('verified_to')}
              InputLabelProps={{ shrink: true }}
              inputProps={{ min: filters.verified_from || undefined, 'aria-label': 'Reviewed to (UTC)' }}
              sx={{ width: 160 }}
            />
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel id="anomaly-sort-label">Sort by</InputLabel>
              <Select
                labelId="anomaly-sort-label"
                value={sortOrder}
                label="Sort by"
                onChange={(e) => { setSortOrder(e.target.value); setPage(1); }}
                inputProps={{ 'aria-label': 'Sort by' }}
              >
                {ANOMALY_SORT_OPTIONS.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
              </Select>
            </FormControl>
            {hasActiveFilters && (
              <Tooltip title="Clear filters">
                <IconButton size="small" onClick={clearFilters}><ClearIcon /></IconButton>
              </Tooltip>
            )}
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', pt: 1, gap: 2 }}>
            <FormControlLabel
              control={<Switch size="small" checked={showNeedsRevalidationOnly} onChange={(e) => { setShowNeedsRevalidationOnly(e.target.checked); setPage(1); }} />}
              label={<Typography variant="caption">Needs Revalidation</Typography>}
            />
            <FormControlLabel
              control={<Switch size="small" checked={showArchived} onChange={(e) => { const on = e.target.checked; setShowArchived(on); setFilters((prev) => filtersOnArchivedToggle(prev, on)); setPage(1); }} />}
              label={<Typography variant="caption">Show Fixed</Typography>}
            />
            <FormControlLabel
              control={<Switch size="small" checked={showClosed} onChange={(e) => { setShowClosed(e.target.checked); setPage(1); }} />}
              label={<Typography variant="caption">Show Closed</Typography>}
            />
            <Typography variant="body2" color="text.secondary">
              {totalFiltered} anomal{totalFiltered === 1 ? 'y' : 'ies'}
            </Typography>
            <Tooltip title="Download every anomaly matching these filters (up to 10,000 rows)">
              <span>
                <Button size="small" variant="outlined" onClick={exportCsv} disabled={exporting} startIcon={<DownloadIcon sx={{ fontSize: 16 }} />}>
                  {exporting ? 'Exporting…' : 'Export CSV'}
                </Button>
              </span>
            </Tooltip>
          </Box>
        </Box>
      </Card>

      {/* Bulk actions */}
      {selectedAnomalies.size > 0 && (
        <Card sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
              <Chip
                label={`${selectedAnomalies.size} selected`}
                size="small"
                color="primary"
                variant="outlined"
              />
              <Button size="small" variant="text" onClick={selectUnassignedOnPage} disabled={bulkAssigning}>
                Select unassigned
              </Button>
              <Button
                size="small"
                variant="text"
                onClick={() => {
                  setSelectedAnomalies(new Set());
                  setAutoDistributeOpen(false);
                  setSelectedDistributeUsers([]);
                }}
                disabled={bulkAssigning}
              >
                Clear
              </Button>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
              <FormControl size="small" sx={{ minWidth: 180 }}>
                <InputLabel>Assign all to</InputLabel>
                <Select
                  value=""
                  label="Assign all to"
                  onChange={(e) => handleBulkAssign(e.target.value)}
                  disabled={bulkAssigning}
                >
                  <MenuItem value=""><em>Unassign all</em></MenuItem>
                  {users.map((u) => (
                    <MenuItem key={u.email} value={u.email}>{u.email}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button
                size="small"
                variant="outlined"
                onClick={() => setAutoDistributeOpen((open) => !open)}
                disabled={bulkAssigning || users.length === 0}
              >
                Auto-distribute
              </Button>
              {bulkAssigning && <CircularProgress size={18} />}
            </Box>
          </Box>

          {autoDistributeOpen && (
            <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
              <Typography variant="body2" fontWeight={600} sx={{ mb: 1.5 }}>
                Distribute {selectedAnomalies.size} anomalies evenly among:
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                {users.map((u) => {
                  const selected = selectedDistributeUsers.includes(u.email);
                  return (
                    <Chip
                      key={u.email}
                      label={u.email.split('@')[0]}
                      onClick={() => toggleDistributeUser(u.email)}
                      color={selected ? 'primary' : 'default'}
                      variant={selected ? 'filled' : 'outlined'}
                      sx={{ cursor: 'pointer' }}
                    />
                  );
                })}
              </Box>
              {selectedDistributeUsers.length > 0 && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Typography variant="caption" color="text.secondary">
                    ~{Math.ceil(selectedAnomalies.size / selectedDistributeUsers.length)} per user
                  </Typography>
                  <Button
                    size="small"
                    variant="contained"
                    onClick={handleAutoDistribute}
                    disabled={bulkAssigning}
                  >
                    Distribute now
                  </Button>
                </Box>
              )}
            </Box>
          )}
        </Card>
      )}

      {/* Table */}
      <Card sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {loading ? (
          <Loading />
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : total === 0 ? (
          <EmptyState title="No anomalies found" description="Adjust filters or import a report to get started." />
        ) : (
          <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
            <Table stickyHeader size="small" minWidth={1350}>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      indeterminate={selectedAnomalies.size > 0 && selectedAnomalies.size < paginatedAnomalies.length}
                      checked={paginatedAnomalies.length > 0 && selectedAnomalies.size === paginatedAnomalies.length}
                      onChange={toggleSelectAll}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>Retailer</TableCell>
                  {!isDeterministic && <TableCell>Attribute</TableCell>}
                  {isDeterministic && <TableCell>Grouping key</TableCell>}
                  <TableCell>Page Type</TableCell>
                  <TableCell>Issue Type</TableCell>
                  <TableCell>Verdict</TableCell>
                  <TableCell>First Seen</TableCell>
                  <TableCell>Last Seen</TableCell>
                  <TableCell align="center">Occurrences</TableCell>
                  <TableCell>Review Status</TableCell>
                  <TableCell>Verified By</TableCell>
                  <TableCell>Assignee</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedAnomalies.map((anomaly) => {
                  const reviewVerdict = getReviewVerdict(anomaly.id);
                  const isSelected = selectedAnomalies.has(anomaly.id);
                  const isFixed = anomaly.heal_status === 'fixed';
                  const isClosed = !!anomaly.is_closed;
                  return (
                    <TableRow
                      key={anomaly.id}
                      hover
                      selected={isSelected}
                      sx={{ '&:last-child td': { borderBottom: 0 }, ...((isFixed || isClosed) && { opacity: 0.55, bgcolor: '#f8fafc' }) }}
                    >
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={isSelected}
                          onChange={() => {
                            setSelectedAnomalies((prev) => {
                              const next = new Set(prev);
                              if (next.has(anomaly.id)) next.delete(anomaly.id);
                              else next.add(anomaly.id);
                              return next;
                            });
                          }}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={500}>{anomaly.retailer}</Typography>
                      </TableCell>
                      {!isDeterministic && (
                      <TableCell>
                        <Typography variant="body2">{anomaly.attribute}</Typography>
                      </TableCell>
                      )}
                      {isDeterministic && (
                      <TableCell>
                        <Typography variant="body2">{anomaly.grouping_key || '—'}</Typography>
                      </TableCell>
                      )}
                      <TableCell>
                        <Chip label={anomaly.page_type || 'detail'} size="small" variant="outlined" />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={issueTypeLabel(anomaly.issue_type)}
                          size="small"
                          color={anomaly.issue_type === 'non-null-issues' ? 'warning' : 'info'}
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        {anomaly.verdict_status === 'conflict' ? (
                          <Tooltip title={`Conflict: was "${anomaly.previous_verdict}", now "${anomaly.verdict}". Click to resolve.`}>
                            <Chip
                              icon={<WarningAmberIcon sx={{ fontSize: '0.85rem' }} />}
                              label="Conflict"
                              size="small"
                              onClick={() => openConflictDialog(anomaly)}
                              sx={{ bgcolor: '#fee2e2', color: '#b91c1c', fontWeight: 600, fontSize: '0.7rem', cursor: 'pointer' }}
                            />
                          </Tooltip>
                        ) : anomaly.verdict ? (
                          <Chip
                            label={anomaly.verdict}
                            size="small"
                            sx={{
                              bgcolor: anomaly.verdict.toLowerCase().includes('true') ? '#fef3c7' : '#e0e7ff',
                              color: anomaly.verdict.toLowerCase().includes('true') ? '#92400e' : '#3730a3',
                              fontWeight: 500,
                              fontSize: '0.7rem',
                            }}
                          />
                        ) : null}
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption">{formatDate(anomaly.first_seen_at)}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption">{formatDate(anomaly.last_seen_at)}</Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={anomaly.occurrence_count || 1}
                          size="small"
                          color={anomaly.occurrence_count > 1 ? 'secondary' : 'default'}
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          {anomalyHealChip(anomaly.heal_status) === 'healed' ? (
                            <Chip
                              label="Healed"
                              size="small"
                              sx={{ bgcolor: '#d1fae5', color: '#065f46', fontWeight: 600 }}
                            />
                          ) : anomalyHealChip(anomaly.heal_status) === OUT_OF_SCOPE ? (
                            <Tooltip title="The healing agent can't heal this retailer's config, so no repair was attempted. See the Job Tracker for the reason.">
                              <Chip
                                label="Out of scope"
                                size="small"
                                sx={{ bgcolor: '#f1f5f9', color: '#475569', fontWeight: 600 }}
                              />
                            </Tooltip>
                          ) : anomalyHealChip(anomaly.heal_status) === FALSE_POSITIVE ? (
                            <Tooltip title="The healing agent found the config already extracts correctly, so this anomaly is not real and nothing was changed. See the Job Tracker for the reason.">
                              <Chip
                                label="False positive"
                                size="small"
                                sx={{ bgcolor: '#eef2ff', color: '#3730a3', fontWeight: 600 }}
                              />
                            </Tooltip>
                          ) : anomalyHealChip(anomaly.heal_status) === 'healing' ? (
                            <Chip
                              label="Healing..."
                              size="small"
                              sx={{ bgcolor: '#dbeafe', color: '#1e40af', fontWeight: 600 }}
                            />
                          ) : reviewVerdict === 'true_anomaly' ? (
                            <Chip
                              label="True Anomaly"
                              size="small"
                              sx={{ bgcolor: '#fee2e2', color: '#b91c1c', fontWeight: 600 }}
                            />
                          ) : reviewVerdict === 'false_positive' ? (
                            <Chip
                              label="False Positive"
                              size="small"
                              sx={{ bgcolor: '#dcfce7', color: '#166534', fontWeight: 600 }}
                            />
                          ) : reviewVerdict === 'reviewed' ? (
                            <Chip
                              label="Reviewed"
                              size="small"
                              sx={{ bgcolor: '#fef3c7', color: '#92400e', fontWeight: 600 }}
                            />
                          ) : (
                            <Chip label="Unreviewed" size="small" variant="outlined" />
                          )}
                          {isClosed && (
                            <Tooltip title={`Closed as false positive${anomaly.closed_by ? ` by ${anomaly.closed_by}` : ''}${anomaly.closed_at ? ` on ${formatDate(anomaly.closed_at)}` : ''}. A new sighting of this key starts a fresh anomaly.`}>
                              <Chip label="Closed" size="small" variant="outlined" sx={{ borderColor: '#94a3b8', color: '#64748b' }} />
                            </Tooltip>
                          )}
                          {anomaly.needs_revalidation && (
                            <Tooltip title="New evidence available since last review. Click to dismiss.">
                              <IconButton size="small" onClick={() => handleDismissRevalidation(anomaly.id)} sx={{ p: 0.25 }}>
                                <NewReleasesIcon sx={{ fontSize: '1rem', color: '#d97706' }} />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell data-testid={`verified-by-${anomaly.id}`}>
                        {anomaly.verified_by ? (
                          <Tooltip title={`${anomaly.verified_by}${anomaly.verified_at ? ` · ${formatDate(anomaly.verified_at)}` : ''}`}>
                            <Typography variant="caption">{anomaly.verified_by.split('@')[0]}</Typography>
                          </Tooltip>
                        ) : (
                          <Typography variant="caption" color="text.secondary">—</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {anomaly.assignee ? (
                          <Tooltip title={anomaly.assignee}>
                            <Chip
                              label={anomaly.assignee.split('@')[0]}
                              size="small"
                              onDelete={() => handleAssign(anomaly.id, null)}
                            />
                          </Tooltip>
                        ) : (
                          <FormControl size="small" sx={{ minWidth: 100 }}>
                            <Select
                              value=""
                              displayEmpty
                              onChange={(e) => handleAssign(anomaly.id, e.target.value)}
                              sx={{ fontSize: '0.75rem' }}
                              renderValue={() => <Typography variant="caption" color="text.secondary">Assign</Typography>}
                            >
                              {users.map((u) => <MenuItem key={u.email} value={u.email}>{u.name || u.email}</MenuItem>)}
                            </Select>
                          </FormControl>
                        )}
                      </TableCell>
                      <TableCell align="center">
                        {isFixed ? (
                          <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', justifyContent: 'center' }}>
                            <Chip label="Fixed" size="small" sx={{ bgcolor: '#d1fae5', color: '#065f46', fontWeight: 600 }} />
                            {isDeterministic ? (
                              <Tooltip title="View metadata">
                                <IconButton size="small" onClick={() => setDetailDialog({ open: true, anomaly })}>
                                  <VisibilityIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            ) : (
                              <Tooltip title="View evidence">
                                <IconButton size="small" onClick={() => openEvidenceModal(anomaly)}>
                                  <VisibilityIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Box>
                        ) : isDeterministic ? (
                          <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                            <Tooltip title="View metadata">
                              <IconButton size="small" onClick={() => setDetailDialog({ open: true, anomaly })}>
                                <VisibilityIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => setClassifyDialog({
                                open: true,
                                anomaly,
                                verdict: anomaly.review_status === 'false_positive' ? 'false_positive' : 'true_anomaly',
                                reason: '',
                              })}
                            >
                              Classify
                            </Button>
                            <Tooltip title="Mark as Fixed">
                              <IconButton size="small" color="primary" onClick={() => setMarkFixedModal({ open: true, anomaly })}>
                                <CheckCircleOutlineIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        ) : (
                        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                          <Tooltip title="View evidence">
                            <IconButton size="small" onClick={() => openEvidenceModal(anomaly)}>
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Mark as Fixed">
                            <IconButton size="small" color="primary" onClick={() => setMarkFixedModal({ open: true, anomaly })}>
                              <CheckCircleOutlineIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          {reviewVerdict === 'true_anomaly' && canTriggerHeal(anomaly.heal_status) && (
                            <Tooltip title={healingInProgress === anomaly.id ? 'Healing in progress...' : anomaly.verdict_status === 'conflict' ? 'Resolve verdict conflict first' : 'Heal — sends only true anomaly samples'}>
                              <span>
                                <IconButton
                                  size="small"
                                  color="success"
                                  disabled={anomaly.verdict_status === 'conflict' || healingInProgress === anomaly.id}
                                  onClick={() => handleHeal(anomaly)}
                                >
                                  {healingInProgress === anomaly.id ? <CircularProgress size={18} color="success" /> : <HealingIcon fontSize="small" />}
                                </IconButton>
                              </span>
                            </Tooltip>
                          )}
                        </Box>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Box>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
            <Pagination
              count={totalPages}
              page={page}
              onChange={(_, p) => setPage(p)}
              size="small"
              color="primary"
              showFirstButton
              showLastButton
            />
          </Box>
        )}
      </Card>

      {/* Evidence Modals */}
      <EvidenceReviewModal
        isOpen={imagePreviewOpen}
        issueType="null-issues"
        onClose={closeEvidenceModal}
        evidenceList={selectedEvidence}
        deepLinkTarget={deepLinkTarget}
        onOccurrenceChange={handleEvidenceOccurrenceChange}
        onInvalidOccurrence={handleEvidenceOccurrenceInvalid}
        sampleClassifications={
          evidenceAnomalyId && sampleClassifications[evidenceAnomalyId]
            ? sampleClassifications[evidenceAnomalyId]
            : selectedEvidence.map((e) => {
                const isAbsent = (e.presence || '').toLowerCase() === 'absent';
                return isAbsent ? { verdict: 'skipped', fpReason: '' } : { verdict: '', fpReason: '' };
              })
        }
        onUpdateVerdict={() => {}}
        onUpdateFpReason={() => {}}
        onPersistClassifications={evidenceAnomalyId ? async (classifications, occurrenceId) => {
          const serialized = JSON.stringify(classifications);
          const res = await api.saveSampleClassifications(evidenceAnomalyId, classifications, occurrenceId || null);
          setAnomalies((prev) => prev.map((a) =>
            a.id === evidenceAnomalyId ? rowAfterClassificationSave(a, serialized, res) : a
          ));
          // A cold deep-linked anomaly is not in the page rows: keep its header current too.
          setSelectedEvidenceMeta((m) => ({ ...m, verifiedBy: verifiedByAfterSave(m.verifiedBy, res) }));
        } : null}
        anomalyId={evidenceAnomalyId}
        retailer={selectedEvidenceMeta.retailer}
        attribute={selectedEvidenceMeta.attribute}
        pageType={selectedEvidenceMeta.pageType}
        verifiedBy={verifiedByFor(evidenceAnomalyId, selectedEvidenceMeta)}
      />
      <EvidenceReviewModal
        isOpen={nonNullEvidenceOpen}
        issueType="non-null-issues"
        onClose={closeEvidenceModal}
        evidenceList={selectedNonNullEvidence}
        retailer={selectedNonNullMeta.retailer}
        attribute={selectedNonNullMeta.attribute}
        pageType={selectedNonNullMeta.pageType}
        verifiedBy={verifiedByFor(nonNullAnomalyId, selectedNonNullMeta)}
        anomalyId={nonNullAnomalyId}
        deepLinkTarget={deepLinkTarget}
        onOccurrenceChange={handleEvidenceOccurrenceChange}
        onInvalidOccurrence={handleEvidenceOccurrenceInvalid}
        sampleClassifications={
          nonNullAnomalyId && sampleClassifications[nonNullAnomalyId]
            ? sampleClassifications[nonNullAnomalyId]
            : null
        }
        onPersistClassifications={nonNullAnomalyId ? async (classifications, occurrenceId) => {
          const serialized = JSON.stringify(classifications);
          const res = await api.saveSampleClassifications(nonNullAnomalyId, classifications, occurrenceId || null);
          setAnomalies((prev) => prev.map((a) =>
            a.id === nonNullAnomalyId ? rowAfterClassificationSave(a, serialized, res) : a
          ));
          setSelectedNonNullMeta((m) => ({ ...m, verifiedBy: verifiedByAfterSave(m.verifiedBy, res) }));
        } : null}
      />

      {/* Conflict Resolution Dialog */}
      <Dialog open={conflictDialog.open} onClose={() => setConflictDialog({ open: false, anomaly: null, chosen: '' })} maxWidth="xs" fullWidth>
        <DialogTitle>Resolve Verdict Conflict</DialogTitle>
        <DialogContent>
          {conflictDialog.anomaly && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                This anomaly received conflicting verdicts across occurrences. Choose which verdict to keep:
              </Typography>
              <RadioGroup
                value={conflictDialog.chosen}
                onChange={(e) => setConflictDialog((prev) => ({ ...prev, chosen: e.target.value }))}
              >
                <FormControlLabel
                  value={conflictDialog.anomaly.previous_verdict || ''}
                  control={<Radio size="small" />}
                  label={
                    <Typography variant="body2">
                      Previous: <strong>{conflictDialog.anomaly.previous_verdict}</strong>
                    </Typography>
                  }
                />
                <FormControlLabel
                  value={conflictDialog.anomaly.verdict || ''}
                  control={<Radio size="small" />}
                  label={
                    <Typography variant="body2">
                      Latest: <strong>{conflictDialog.anomaly.verdict}</strong>
                    </Typography>
                  }
                />
              </RadioGroup>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                Resolving will reset any existing classifications — you will need to re-validate.
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button size="small" onClick={() => setConflictDialog({ open: false, anomaly: null, chosen: '' })}>
            Cancel
          </Button>
          <Button size="small" variant="contained" onClick={handleResolveConflict} disabled={!conflictDialog.chosen}>
            Resolve
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={detailDialog.open} onClose={() => setDetailDialog({ open: false, anomaly: null })} maxWidth="md" fullWidth>
        <DialogTitle>
          {detailDialog.anomaly
            ? `${detailDialog.anomaly.retailer} · ${issueTypeLabel(detailDialog.anomaly.issue_type)}`
            : 'Details'}
        </DialogTitle>
        <DialogContent>
          {detailDialog.anomaly && (() => {
            const Panel = getIssueTypeRenderer(detailDialog.anomaly.issue_type);
            return <Panel payload={detailDialog.anomaly.payload} />;
          })()}
        </DialogContent>
        <DialogActions>
          <Button size="small" onClick={() => setDetailDialog({ open: false, anomaly: null })}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={classifyDialog.open} onClose={() => setClassifyDialog({ open: false, anomaly: null, verdict: 'true_anomaly', reason: '' })} maxWidth="xs" fullWidth>
        <DialogTitle>Classify anomaly</DialogTitle>
        <DialogContent>
          <RadioGroup
            value={classifyDialog.verdict}
            onChange={(e) => setClassifyDialog((prev) => ({ ...prev, verdict: e.target.value }))}
          >
            <FormControlLabel value="true_anomaly" control={<Radio size="small" />} label="True anomaly" />
            <FormControlLabel value="false_positive" control={<Radio size="small" />} label="False positive" />
          </RadioGroup>
          <TextField
            label={classifyDialog.verdict === 'false_positive' ? 'Reason (required)' : 'Reason (optional)'}
            fullWidth
            required={classifyDialog.verdict === 'false_positive'}
            multiline
            minRows={2}
            value={classifyDialog.reason}
            onChange={(e) => setClassifyDialog((prev) => ({ ...prev, reason: e.target.value }))}
            error={classifyDialog.verdict === 'false_positive' && !String(classifyDialog.reason || '').trim()}
            helperText={classifyDialog.verdict === 'false_positive' && !String(classifyDialog.reason || '').trim()
              ? 'A reason is required for false positives'
              : ''}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button size="small" onClick={() => setClassifyDialog({ open: false, anomaly: null, verdict: 'true_anomaly', reason: '' })}>Cancel</Button>
          <Button
            size="small"
            variant="contained"
            onClick={submitClassify}
            disabled={classifying || (classifyDialog.verdict === 'false_positive' && !String(classifyDialog.reason || '').trim())}
          >
            {classifying ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Mark Fixed Modal */}
      <MarkFixedModal
        isOpen={markFixedModal.open}
        onClose={() => setMarkFixedModal({ open: false, anomaly: null })}
        anomaly={markFixedModal.anomaly}
        onMarkedFixed={() => {
          setMarkFixedModal({ open: false, anomaly: null });
          setSnackbar({ open: true, message: 'Anomaly marked as fixed', severity: 'success' });
          fetchAnomalies();
        }}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        message={snackbar.message}
      />
    </Box>
  );
}

export default AnomaliesTab;
